import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';
import { compose } from 'underscore';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import connectToRouter from 'js/lib/connectToRouter';
import waitFor from 'js/lib/waitFor';

import { Grid, Pagination } from '@coursera/cds-core';

import { loadThreads } from 'bundles/discussions/actions/ThreadsActions';
import discussionsForumsHOC from 'bundles/discussions/components/discussionsForumsHOC';
import { ControlsBar } from 'bundles/discussions/components/forumsV2-ControlsBar';
import { ListBody } from 'bundles/discussions/components/forumsV2-ListBody';
// might need to fix this file bundles/discussions/components/personalization-NewThreadButton
import { NewThreadButtonNaptimeContainer as NewThreadButton } from 'bundles/discussions/components/personalization-NewThreadButton';
import ThreadsListEntry from 'bundles/discussions/components/personalization-discussionsBody/ThreadsListEntry';
import type { LoadingState, QuestionSort } from 'bundles/discussions/constants';
import * as discussionsUrl from 'bundles/discussions/utils/discussionsUrl';
import { handlePaginationAriaLabel } from 'bundles/discussions/utils/handlePaginationAriaLabel';
import routerConnectToCurrentForum from 'bundles/discussions/utils/routerConnectToCurrentForum';
import unreadTracker from 'bundles/discussions/utils/unreadTracker';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import type GroupForumsV1 from 'bundles/naptimejs/resources/groupForums.v1';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import type OnDemandCourseForumsV1 from 'bundles/naptimejs/resources/onDemandCourseForums.v1';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import type OnDemandMentorForumsV1 from 'bundles/naptimejs/resources/onDemandMentorForums.v1';

import _t from 'i18n!nls/discussions';

import 'css!bundles/discussions/components/discussionsBody/__styles__/ThreadsList';

type ForumTypes = OnDemandCourseForumsV1 | OnDemandMentorForumsV1 | GroupForumsV1;

type Props = {
  courseForums: OnDemandCourseForumsV1[];
  mentorForums: OnDemandMentorForumsV1[];
  groupForums: GroupForumsV1[];
  currentForumId: string;
  currentForum: ForumTypes;
  subForumId?: string;
  subForum?: ForumTypes;
  sort: QuestionSort;
  answered?: boolean;
  page: number;
  entries: [];
  loadingState: LoadingState;
  pageCount: number;
  activeFilterQueryString: string;
  userId: number;
  hasModerationRole: boolean;
  currentForumUrl: string;
};

type State = {
  inSearch: boolean;
};
const PAGE_SIZE = 15;
class ThreadsList extends React.Component<Props, State> {
  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
    router: PropTypes.object.isRequired,
  };

  declare context: LegacyContextType<typeof ThreadsList.contextTypes>;

  state = {
    inSearch: false,
  };

  componentWillMount() {
    const { sort, answered, page, currentForum, subForum, activeFilterQueryString } = this.props;
    const loadableForum = subForum || currentForum;
    this.context.executeAction(loadThreads, {
      filterQueryString: activeFilterQueryString,
      sort,
      answered,
      pageNum: page,
      forumType: loadableForum && loadableForum.forumType.typeName,
      forumId: loadableForum && loadableForum.id,
      userId: this.props.userId,
      includeDeleted: this.props.hasModerationRole,
    });
  }

  entriesChanged = (entries: $TSFixMe, nextEntries: $TSFixMe) => {
    let changed = false;
    if (entries.length !== nextEntries.length) {
      changed = true;
    }
    if (entries[0]?.id !== nextEntries[0]?.id) {
      changed = true;
    }
    return changed;
  };

  componentWillUpdate(nextProps: $TSFixMe, nextState: $TSFixMe, nextContext: $TSFixMe) {
    const { subForum } = this.props;
    let loadableForum = nextProps.currentForum;

    if (subForum) {
      loadableForum = subForum;
    }

    if (
      this.props.sort !== nextProps.sort ||
      this.props.answered !== nextProps.answered ||
      this.props.page !== nextProps.page ||
      this.props.currentForumId !== nextProps.currentForumId ||
      this.props.subForumId !== nextProps.subForumId ||
      this.props.subForum?.id !== nextProps.subForum?.id ||
      this.props.activeFilterQueryString !== nextProps.activeFilterQueryString ||
      this.entriesChanged(this.props.entries, nextProps.entries)
    ) {
      this.context.executeAction(loadThreads, {
        filterQueryString: nextProps.activeFilterQueryString,
        sort: nextProps.sort,
        answered: nextProps.answered,
        pageNum: nextProps.page,
        forumType: loadableForum && loadableForum.forumType.typeName,
        forumId: loadableForum && loadableForum.id,
        userId: this.props.userId,
        includeDeleted: this.props.hasModerationRole,
      });
    }
  }

  onPageChange = (page: $TSFixMe) => {
    const { router } = this.context;
    router.push({
      pathname: this.context.router.location.pathname,
      params: {},
      query: Object.assign(this.context.router.location.query, { page }),
    });
  };

  toggleSearch = (currentState: boolean) => {
    this.setState({
      inSearch: !currentState,
    });
  };

  renderEntry = (entry: $TSFixMe) => {
    const { activeFilterQueryString, courseForums, mentorForums, groupForums, currentForum, currentForumUrl } =
      this.props;

    const isAnswered = entry.lastAnsweredAt && entry.lastAnsweredBy;
    const isRead = !unreadTracker.hasUnread(entry);
    const profile = isAnswered ? entry.lastAnsweredBy : entry.creator;
    const metadataLabel = _t('posted');
    const forums = courseForums.concat(mentorForums).concat(groupForums);
    const forumForEntry = forums.find((forum) => forum.forumId === entry.forumId);
    const forumName = (forumForEntry && currentForum.id !== forumForEntry.id && forumForEntry.title) || undefined;
    const discussionsLink = discussionsUrl.getDeepLink(currentForumUrl, entry);
    const hasAnswerCount = typeof entry.answerCount !== 'undefined' && !isNaN(entry.answerCount);
    const answerCount = hasAnswerCount ? entry.answerCount : entry.topLevelAnswerCount;

    return (
      <ThreadsListEntry
        key={entry.id + activeFilterQueryString}
        threadUrl={discussionsLink}
        profile={profile}
        timestamp={entry.lastAnsweredAt || entry.createdAt}
        threadName={entry.content.question}
        questionId={entry.id}
        questionUserId={entry.creatorId}
        entry={entry}
        isPinned={!!(entry.state && entry.state.pinned)}
        isDeleted={!!(entry.state && entry.state.deleted)}
        isRead={isRead}
        forumName={forumName}
        metadataLabel={metadataLabel}
        viewCount={Math.max(answerCount, entry.viewCount)}
        answerCount={answerCount}
        badge={entry.answerBadge.answerBadge}
      />
    );
  };

  render() {
    const showPaginationControls = !(this.props.pageCount !== undefined && this.props.pageCount <= 1);

    const maxCount = PAGE_SIZE * this.props.pageCount;
    return (
      <Grid container className="rc-ThreadsList" css={{ maxWidth: '650px' }}>
        <Grid item xs={12}>
          <ControlsBar
            currentForum={this.props.currentForum}
            inSearch={this.state.inSearch}
            threadsListLoadingState={this.props.loadingState}
          />
          <ListBody
            emptyStatePlaceholderText={_t('No Posts Yet')}
            fossilized={this.state.inSearch}
            loadingState={this.props.loadingState}
          >
            {this.props.entries && this.props.entries.map(this.renderEntry)}
          </ListBody>
        </Grid>
        {showPaginationControls && (
          <Grid
            item
            xs={12}
            css={{ justifyContent: 'center', marginTop: '32px', marginBottom: '6px', textAlign: 'center' }}
          >
            <Pagination
              getItemAriaLabel={handlePaginationAriaLabel}
              onPageChange={(e, page) => this.onPageChange(page)}
              total={maxCount}
              page={this.props.page}
              pageSize={PAGE_SIZE}
              hidePageSizeInput={true}
            />
          </Grid>
        )}
      </Grid>
    );
  }
}

export default compose(
  connectToStores(
    ['SessionFilterStore', 'ThreadsStore', 'ApplicationStore'],
    ({ SessionFilterStore, ThreadsStore, ApplicationStore }) => ({
      activeFilterQueryString: SessionFilterStore.activeFilterQueryString,
      entries: ThreadsStore.getThreadsPage(),
      loadingState: ThreadsStore.getLoadingState(),
      pageCount: ThreadsStore.getPageCount(),
      userId: ApplicationStore.getUserData().id,
    })
  ),
  discussionsForumsHOC({
    fields: ['title', 'link', 'forumType'],
    subcomponents: [NewThreadButton],
  }),
  connectToRouter(routerConnectToCurrentForum),
  waitFor((props) => !!(props.activeFilterQueryString !== undefined && props.sort && props.page && props.currentForum))
)(ThreadsList);
